import React, { useState, useEffect, useCallback } from 'react';
import Icon from '../../components/AppIcon';
import { tesoreriaService, cuentasService, gastosRecurrentesService, centrosCosteService } from '../../services/financialService';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';

const VIEWS = ['Posición Diaria', 'Previsión de Caja', 'Gastos Recurrentes', 'Histórico'];
const PERIODS = ['Semana', 'Mes', 'Trimestre', 'Año'];
const PERIOD_MAP = { 'Semana': 'week', 'Mes': 'month', 'Trimestre': 'quarter', 'Año': 'year' };
const PERIODICIDADES = ['MENSUAL', 'BIMESTRAL', 'TRIMESTRAL', 'SEMESTRAL', 'ANUAL'];
const CATEGORIAS_REC = ['ALQUILER','ELECTRICIDAD','AGUA','GESTORIA','SEGUROS','TELEFONIA_INTERNET','SOFTWARE','SEGURIDAD_SOCIAL','PERSONAL','COMISIONES_BANCARIAS','TRANSPORTE','COMBUSTIBLE','MANTENIMIENTO','REPARACIONES','PUBLICIDAD','MATERIAL','SERVICIOS_PROFESIONALES','IMPUESTOS','MERCANCIA','OTROS'];

const fmt = (n) => (typeof n === 'number' ? n?.toLocaleString('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '0,00');
const fmtEur = (n) => `${fmt(n)} €`;

// ─── MODAL CUENTA ─────────────────────────────────────────────────────────────
const CuentaFormModal = ({ onClose, onSave }) => {
  const [form, setForm] = useState({ nombre: '', tipo: 'CUENTA_BANCARIA', banco: '', iban: '', titular: '', saldo_inicial: 0 });
  const [saving, setSaving] = useState(false);
  const inputCls = 'w-full px-3 py-2 rounded-xl text-xs font-oranienbaum text-white/80 outline-none bg-transparent';
  const inputStyle = { background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.10)' };
  const handleSave = async () => {
    setSaving(true);
    try { await onSave({ ...form, saldo_inicial: parseFloat(form?.saldo_inicial) || 0, saldo_actual: parseFloat(form?.saldo_inicial) || 0 }); onClose(); }
    catch (err) { console.error(err); } finally { setSaving(false); }
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.7)' }}>
      <div className="w-full max-w-md rounded-2xl p-6" style={{ background: '#141414', border: '1px solid rgba(255,255,255,0.10)' }}>
        <div className="flex items-center justify-between mb-5">
          <h2 className="font-oranienbaum font-bold text-white text-lg">Nueva Cuenta</h2>
          <button onClick={onClose} className="text-white/40 hover:text-white/80"><Icon name="X" size={18} /></button>
        </div>
        <div className="space-y-3">
          {[{ label: 'Nombre *', key: 'nombre', type: 'text' }, { label: 'Banco', key: 'banco', type: 'text' }, { label: 'IBAN', key: 'iban', type: 'text' }, { label: 'Titular', key: 'titular', type: 'text' }, { label: 'Saldo Inicial (€)', key: 'saldo_inicial', type: 'number' }]?.map(f => (
            <div key={f?.key}>
              <label className="block font-oranienbaum text-white/40 text-[10px] uppercase tracking-wider mb-1">{f?.label}</label>
              <input type={f?.type} value={form?.[f?.key]} onChange={e => setForm(p => ({ ...p, [f?.key]: e?.target?.value }))} className={inputCls} style={inputStyle} />
            </div>
          ))}
          <div>
            <label className="block font-oranienbaum text-white/40 text-[10px] uppercase tracking-wider mb-1">Tipo</label>
            <select value={form?.tipo} onChange={e => setForm(p => ({ ...p, tipo: e?.target?.value }))} className={inputCls} style={inputStyle}>
              {['CUENTA_BANCARIA', 'CAJA_EFECTIVO', 'OTRA_CUENTA']?.map(t => <option key={t} value={t} style={{ background: '#141414' }}>{t?.replace(/_/g, ' ')}</option>)}
            </select>
          </div>
        </div>
        <div className="flex justify-end gap-3 mt-5">
          <button onClick={onClose} className="px-4 py-2 rounded-xl text-xs font-oranienbaum text-white/50" style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)' }}>Cancelar</button>
          <button onClick={handleSave} disabled={saving} className="px-4 py-2 rounded-xl text-xs font-oranienbaum font-semibold text-black disabled:opacity-50" style={{ background: '#D6EF4E' }}>
            {saving ? 'Guardando...' : 'Guardar'}
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── MODAL MOVIMIENTO ─────────────────────────────────────────────────────────
const MovimientoFormModal = ({ onClose, onSave, cuentas }) => {
  const [form, setForm] = useState({ fecha: new Date()?.toISOString()?.split('T')?.[0], concepto: '', tipo: 'GASTO', entrada: 0, salida: 0, cuenta_id: cuentas?.[0]?.id || '' });
  const [saving, setSaving] = useState(false);
  const inputCls = 'w-full px-3 py-2 rounded-xl text-xs font-oranienbaum text-white/80 outline-none bg-transparent';
  const inputStyle = { background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.10)' };
  const handleSave = async () => {
    setSaving(true);
    try { await onSave({ ...form, entrada: parseFloat(form?.entrada) || 0, salida: parseFloat(form?.salida) || 0 }); onClose(); }
    catch (err) { console.error(err); } finally { setSaving(false); }
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.7)' }}>
      <div className="w-full max-w-md rounded-2xl p-6" style={{ background: '#141414', border: '1px solid rgba(255,255,255,0.10)' }}>
        <div className="flex items-center justify-between mb-5">
          <h2 className="font-oranienbaum font-bold text-white text-lg">Nuevo Movimiento</h2>
          <button onClick={onClose} className="text-white/40 hover:text-white/80"><Icon name="X" size={18} /></button>
        </div>
        <div className="space-y-3">
          <div>
            <label className="block font-oranienbaum text-white/40 text-[10px] uppercase tracking-wider mb-1">Cuenta</label>
            <select value={form?.cuenta_id} onChange={e => setForm(p => ({ ...p, cuenta_id: e?.target?.value }))} className={inputCls} style={inputStyle}>
              {cuentas?.map(c => <option key={c?.id} value={c?.id} style={{ background: '#141414' }}>{c?.nombre}</option>)}
            </select>
          </div>
          <div>
            <label className="block font-oranienbaum text-white/40 text-[10px] uppercase tracking-wider mb-1">Tipo</label>
            <select value={form?.tipo} onChange={e => setForm(p => ({ ...p, tipo: e?.target?.value }))} className={inputCls} style={inputStyle}>
              {['INGRESO', 'GASTO', 'TRANSFERENCIA', 'AJUSTE']?.map(t => <option key={t} value={t} style={{ background: '#141414' }}>{t}</option>)}
            </select>
          </div>
          {[{ label: 'Fecha', key: 'fecha', type: 'date' }, { label: 'Concepto', key: 'concepto', type: 'text' }, { label: 'Entrada (€)', key: 'entrada', type: 'number' }, { label: 'Salida (€)', key: 'salida', type: 'number' }]?.map(f => (
            <div key={f?.key}>
              <label className="block font-oranienbaum text-white/40 text-[10px] uppercase tracking-wider mb-1">{f?.label}</label>
              <input type={f?.type} value={form?.[f?.key]} onChange={e => setForm(p => ({ ...p, [f?.key]: e?.target?.value }))} className={inputCls} style={inputStyle} />
            </div>
          ))}
        </div>
        <div className="flex justify-end gap-3 mt-5">
          <button onClick={onClose} className="px-4 py-2 rounded-xl text-xs font-oranienbaum text-white/50" style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)' }}>Cancelar</button>
          <button onClick={handleSave} disabled={saving} className="px-4 py-2 rounded-xl text-xs font-oranienbaum font-semibold text-black disabled:opacity-50" style={{ background: '#60A5FA' }}>
            {saving ? 'Guardando...' : 'Guardar'}
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── MODAL GASTO RECURRENTE ───────────────────────────────────────────────────
const RecurrenteFormModal = ({ onClose, onSave, centros, initial }) => {
  const [form, setForm] = useState({
    nombre: '', proveedor: '', proveedor_cif: '',
    categoria: 'OTROS', tipo: 'FIJO',
    centro_coste_id: '',
    importe_habitual: '', iva_porcentaje: 21,
    periodicidad: 'MENSUAL',
    periodicidad_pago: 'MENSUAL',
    periodicidad_imputacion: 'MENSUAL',
    fecha_inicio: new Date()?.toISOString()?.split('T')?.[0],
    proximo_vencimiento: '',
    dia_habitual_pago: 1,
    forma_pago: '',
    importe_estimado: false,
    activo: true,
    observaciones: '',
    ...initial,
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const inputCls = 'w-full px-3 py-2 rounded-xl text-xs font-oranienbaum text-white/80 outline-none bg-transparent';
  const inputStyle = { background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.10)' };

  const handleSave = async () => {
    if (!form?.nombre || !form?.importe_habitual) { setError('Nombre e importe son obligatorios'); return; }
    setSaving(true); setError(null);
    try {
      const payload = {
        ...form,
        importe_habitual: parseFloat(form?.importe_habitual) || 0,
        iva_porcentaje: parseFloat(form?.iva_porcentaje) || 21,
        dia_habitual_pago: parseInt(form?.dia_habitual_pago) || 1,
        centro_coste_id: form?.centro_coste_id || null,
        proximo_vencimiento: form?.proximo_vencimiento || form?.fecha_inicio || null,
        forma_pago: form?.forma_pago || null,
      };
      await onSave(payload);
      onClose();
    } catch (err) { setError(err?.message || 'Error al guardar'); } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.7)' }}>
      <div className="w-full max-w-2xl rounded-2xl p-6 max-h-[90vh] overflow-y-auto" style={{ background: '#141414', border: '1px solid rgba(255,255,255,0.10)' }}>
        <div className="flex items-center justify-between mb-5">
          <h2 className="font-oranienbaum font-bold text-white text-lg">{initial ? 'Editar' : 'Nuevo'} Gasto Recurrente</h2>
          <button onClick={onClose} className="text-white/40 hover:text-white/80"><Icon name="X" size={18} /></button>
        </div>
        {error && <div className="mb-3 p-3 rounded-xl text-xs font-oranienbaum text-red-400" style={{ background: 'rgba(248,113,113,0.08)', border: '1px solid rgba(248,113,113,0.20)' }}>{error}</div>}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {[
            { label: 'Nombre / Concepto *', key: 'nombre', type: 'text', full: true },
            { label: 'Proveedor', key: 'proveedor', type: 'text' },
            { label: 'CIF/NIF Proveedor', key: 'proveedor_cif', type: 'text' },
            { label: 'Importe Habitual (€) *', key: 'importe_habitual', type: 'number' },
            { label: 'IVA (%)', key: 'iva_porcentaje', type: 'number' },
            { label: 'Fecha Inicio', key: 'fecha_inicio', type: 'date' },
            { label: 'Próximo Vencimiento', key: 'proximo_vencimiento', type: 'date' },
            { label: 'Día Habitual de Pago', key: 'dia_habitual_pago', type: 'number' },
            { label: 'Observaciones', key: 'observaciones', type: 'text', full: true },
          ]?.map(f => (
            <div key={f?.key} className={f?.full ? 'md:col-span-2' : ''}>
              <label className="block font-oranienbaum text-white/40 text-[10px] uppercase tracking-wider mb-1">{f?.label}</label>
              <input type={f?.type} value={form?.[f?.key] || ''} onChange={e => setForm(p => ({ ...p, [f?.key]: e?.target?.value }))} className={inputCls} style={inputStyle} />
            </div>
          ))}
          {[
            { label: 'Categoría', key: 'categoria', options: CATEGORIAS_REC },
            { label: 'Tipo', key: 'tipo', options: ['FIJO', 'VARIABLE'] },
            { label: 'Periodicidad Pago', key: 'periodicidad_pago', options: PERIODICIDADES },
            { label: 'Periodicidad Imputación', key: 'periodicidad_imputacion', options: PERIODICIDADES },
            { label: 'Forma de Pago', key: 'forma_pago', options: ['', 'TRANSFERENCIA', 'DOMICILIACION', 'EFECTIVO', 'TARJETA', 'CHEQUE', 'OTRO'] },
          ]?.map(f => (
            <div key={f?.key}>
              <label className="block font-oranienbaum text-white/40 text-[10px] uppercase tracking-wider mb-1">{f?.label}</label>
              <select value={form?.[f?.key] || ''} onChange={e => setForm(p => ({ ...p, [f?.key]: e?.target?.value }))} className={inputCls} style={inputStyle}>
                {f?.options?.map(o => <option key={o} value={o} style={{ background: '#141414' }}>{o || '— Sin especificar —'}</option>)}
              </select>
            </div>
          ))}
          <div>
            <label className="block font-oranienbaum text-white/40 text-[10px] uppercase tracking-wider mb-1">Centro de Coste</label>
            <select value={form?.centro_coste_id || ''} onChange={e => setForm(p => ({ ...p, centro_coste_id: e?.target?.value }))} className={inputCls} style={inputStyle}>
              <option value="" style={{ background: '#141414' }}>— Sin asignar —</option>
              {centros?.map(c => <option key={c?.id} value={c?.id} style={{ background: '#141414' }}>{c?.nombre}</option>)}
            </select>
          </div>
          <div className="flex items-center gap-3 pt-4">
            <input type="checkbox" id="importe_estimado" checked={form?.importe_estimado} onChange={e => setForm(p => ({ ...p, importe_estimado: e?.target?.checked }))} className="w-4 h-4 rounded" />
            <label htmlFor="importe_estimado" className="font-oranienbaum text-white/60 text-xs">Importe estimado (variable)</label>
          </div>
          <div className="flex items-center gap-3 pt-4">
            <input type="checkbox" id="activo" checked={form?.activo} onChange={e => setForm(p => ({ ...p, activo: e?.target?.checked }))} className="w-4 h-4 rounded" />
            <label htmlFor="activo" className="font-oranienbaum text-white/60 text-xs">Activo</label>
          </div>
        </div>
        {/* Nota sobre periodicidad */}
        <div className="mt-3 p-3 rounded-xl" style={{ background: 'rgba(214,239,78,0.05)', border: '1px solid rgba(214,239,78,0.15)' }}>
          <p className="font-oranienbaum text-[#D6EF4E]/70 text-[10px]">
            <strong>Periodicidad Pago:</strong> cuándo sale el dinero realmente. <strong>Periodicidad Imputación:</strong> a qué periodo económico corresponde el coste (ej: seguro anual → pago ANUAL, imputación MENSUAL = 1/12 cada mes).
          </p>
        </div>
        <div className="flex justify-end gap-3 mt-5">
          <button onClick={onClose} className="px-4 py-2 rounded-xl text-xs font-oranienbaum text-white/50" style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)' }}>Cancelar</button>
          <button onClick={handleSave} disabled={saving} className="px-4 py-2 rounded-xl text-xs font-oranienbaum font-semibold text-black disabled:opacity-50" style={{ background: '#D6EF4E' }}>
            {saving ? 'Guardando...' : 'Guardar'}
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── PÁGINA PRINCIPAL ─────────────────────────────────────────────────────────
const FinTesoreria = () => {
  const [view, setView] = useState('Posición Diaria');
  const [period, setPeriod] = useState('Mes');
  const [showCuentaForm, setShowCuentaForm] = useState(false);
  const [showMovForm, setShowMovForm] = useState(false);
  const [showRecurrenteForm, setShowRecurrenteForm] = useState(false);
  const [editingRecurrente, setEditingRecurrente] = useState(null);

  const [kpis, setKpis] = useState(null);
  const [previsiones, setPrevisiones] = useState(null);
  const [cuentas, setCuentas] = useState([]);
  const [movimientos, setMovimientos] = useState([]);
  const [recurrentes, setRecurrentes] = useState([]);
  const [centros, setCentros] = useState([]);
  const [generando, setGenerando] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const loadData = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const periodKey = PERIOD_MAP?.[period] || 'month';
      const [kpisData, prevData, cuentasData, movsData, recData, centrosData] = await Promise.all([
        tesoreriaService?.getKPIs({ period: periodKey }),
        tesoreriaService?.getPrevisiones(90),
        cuentasService?.getAll(),
        tesoreriaService?.getMovimientos({ period: periodKey }),
        gastosRecurrentesService?.getAll(),
        centrosCosteService?.getAll(),
      ]);
      setKpis(kpisData); setPrevisiones(prevData); setCuentas(cuentasData);
      setMovimientos(movsData); setRecurrentes(recData); setCentros(centrosData);
    } catch (err) { setError(err?.message || 'Error al cargar tesorería'); }
    finally { setLoading(false); }
  }, [period]);

  useEffect(() => { loadData(); }, [loadData]);

  const handleGenerarPrevisiones = async () => {
    setGenerando(true);
    try {
      const n = await gastosRecurrentesService?.generarPrevisiones(90);
      alert(`${n} previsión(es) generada(s) correctamente`);
      loadData();
    } catch (err) { alert('Error: ' + err?.message); }
    finally { setGenerando(false); }
  };

  const handleDeleteRecurrente = async (id) => {
    if (!window.confirm('¿Cancelar este gasto recurrente y sus previsiones pendientes?')) return;
    try { await gastosRecurrentesService?.delete(id); loadData(); }
    catch (err) { alert('Error: ' + err?.message); }
  };

  const kpiCards = [
    { label: 'Tesorería Disponible', value: fmtEur(kpis?.tesoreriaDisponible || 0), icon: 'Wallet', color: '#D6EF4E', desc: 'Bancario + Caja' },
    { label: 'Saldo Bancario', value: fmtEur(kpis?.saldoBancario || 0), icon: 'Landmark', color: '#34D399', desc: 'Cuentas bancarias' },
    { label: 'Caja Efectivo', value: fmtEur(kpis?.caja || 0), icon: 'DollarSign', color: '#FBBF24', desc: 'Efectivo disponible' },
    { label: 'Ingresos Periodo', value: fmtEur(kpis?.ingresos || 0), icon: 'ArrowDownCircle', color: '#34D399', desc: 'Entradas reales' },
    { label: 'Gastos Periodo', value: fmtEur(kpis?.gastos || 0), icon: 'ArrowUpCircle', color: '#F87171', desc: 'Salidas reales' },
    { label: 'Cashflow Neto', value: fmtEur(kpis?.cashflow || 0), icon: 'Activity', color: '#A78BFA', desc: 'Ingresos - Gastos' },
    { label: 'Cobros Pendientes', value: fmtEur(kpis?.cobrosPendientes || 0), icon: 'Clock', color: '#60A5FA', desc: 'Facturas emitidas' },
    { label: 'Pagos Pendientes', value: fmtEur(kpis?.pagosPendientes || 0), icon: 'AlertTriangle', color: '#F87171', desc: 'Gastos pendientes' },
  ];

  const forecastCards = [
    { label: 'Previsión 30 días', value: fmtEur(previsiones?.prevision30 || 0), color: '#34D399', icon: 'Calendar' },
    { label: 'Previsión 60 días', value: fmtEur(previsiones?.prevision60 || 0), color: '#FBBF24', icon: 'Calendar' },
    { label: 'Previsión 90 días', value: fmtEur(previsiones?.prevision90 || 0), color: (previsiones?.prevision90 || 0) < 0 ? '#F87171' : '#60A5FA', icon: 'Calendar' },
    { label: 'Fecha Tesorería Negativa', value: previsiones?.fechaNegativa || 'No prevista', color: previsiones?.fechaNegativa ? '#F87171' : '#34D399', icon: 'AlertTriangle' },
  ];

  const chartData = (previsiones?.proyeccion || [])?.slice(0, 30)?.map(p => ({
    fecha: p?.fecha?.slice(5), saldo: Math.round(p?.saldo * 100) / 100,
  }));

  return (
    <div className="min-h-screen px-4 py-6 md:px-8"
      style={{ background: 'linear-gradient(135deg, #0A0A0A 0%, #111111 50%, #0D0D0D 100%)' }}>
      {showCuentaForm && <CuentaFormModal onClose={() => setShowCuentaForm(false)} onSave={async (p) => { await cuentasService?.create(p); loadData(); }} />}
      {showMovForm && <MovimientoFormModal onClose={() => setShowMovForm(false)} cuentas={cuentas} onSave={async (p) => { await tesoreriaService?.createMovimiento(p); loadData(); }} />}
      {(showRecurrenteForm || editingRecurrente) && (
        <RecurrenteFormModal
          onClose={() => { setShowRecurrenteForm(false); setEditingRecurrente(null); }}
          centros={centros}
          initial={editingRecurrente}
          onSave={async (p) => {
            if (editingRecurrente?.id) await gastosRecurrentesService?.update(editingRecurrente?.id, p);
            else await gastosRecurrentesService?.create(p);
            loadData();
          }}
        />
      )}
      {/* Breadcrumb */}
      <div className="flex items-center gap-2 mb-5 text-xs font-oranienbaum text-white/30">
        <Icon name="Home" size={12} /><span>/</span>
        <span className="text-[#D6EF4E]/60">Finanzas Corporativas</span>
        <span>/</span><span className="text-white/50">Tesorería</span>
      </div>
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
            style={{ background: 'rgba(96,165,250,0.12)', border: '1px solid rgba(96,165,250,0.25)' }}>
            <Icon name="Wallet" size={20} className="text-[#60A5FA]" />
          </div>
          <div>
            <h1 className="font-oranienbaum font-bold text-white text-2xl tracking-wide">Tesorería</h1>
            <p className="font-oranienbaum text-white/40 text-xs">Centro de control del dinero real · Cashflow y previsiones</p>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="flex items-center gap-1 p-1 rounded-xl" style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)' }}>
            {PERIODS?.map(p => (
              <button key={p} onClick={() => setPeriod(p)}
                className={`px-3 py-1.5 rounded-lg text-xs font-oranienbaum transition-all ${period === p ? 'text-black font-semibold' : 'text-white/50 hover:text-white/80'}`}
                style={period === p ? { background: '#D6EF4E' } : {}}>
                {p}
              </button>
            ))}
          </div>
          <button onClick={() => setShowMovForm(true)} className="flex items-center gap-2 px-3 py-2 rounded-xl text-xs font-oranienbaum font-semibold text-black" style={{ background: '#D6EF4E' }}>
            <Icon name="Plus" size={13} />Movimiento
          </button>
          <button onClick={() => setShowCuentaForm(true)} className="flex items-center gap-2 px-3 py-2 rounded-xl text-xs font-oranienbaum text-white/60 hover:text-white transition-colors"
            style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)' }}>
            <Icon name="Plus" size={13} />Cuenta
          </button>
          <button onClick={loadData} className="flex items-center gap-2 px-3 py-2 rounded-xl text-xs font-oranienbaum text-white/60 hover:text-white transition-colors"
            style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)' }}>
            <Icon name="RefreshCw" size={13} />
          </button>
        </div>
      </div>
      {error && (
        <div className="mb-4 p-3 rounded-xl flex items-center gap-3" style={{ background: 'rgba(248,113,113,0.08)', border: '1px solid rgba(248,113,113,0.20)' }}>
          <Icon name="AlertCircle" size={15} className="text-red-400 flex-shrink-0" />
          <p className="font-oranienbaum text-red-400 text-xs">{error}</p>
        </div>
      )}
      {/* KPI Grid */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
        {kpiCards?.map((kpi, i) => (
          <div key={i} className="rounded-2xl p-4 flex flex-col gap-2"
            style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)' }}>
            <div className="flex items-center justify-between">
              <div className="w-8 h-8 rounded-lg flex items-center justify-center"
                style={{ background: `${kpi?.color}15`, border: `1px solid ${kpi?.color}25` }}>
                <Icon name={kpi?.icon} size={14} style={{ color: kpi?.color }} />
              </div>
            </div>
            <p className="font-oranienbaum font-bold text-white/80 text-lg">{loading ? '...' : kpi?.value}</p>
            <div>
              <p className="font-oranienbaum text-white/50 text-[10px] font-semibold">{kpi?.label}</p>
              <p className="font-oranienbaum text-white/25 text-[9px]">{kpi?.desc}</p>
            </div>
          </div>
        ))}
      </div>
      {/* Forecast strip */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
        {forecastCards?.map((f, i) => (
          <div key={i} className="rounded-xl p-3 flex items-center gap-3"
            style={{ background: 'rgba(255,255,255,0.025)', border: `1px solid ${f?.color}20` }}>
            <div className="w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: `${f?.color}15` }}>
              <Icon name={f?.icon} size={13} style={{ color: f?.color }} />
            </div>
            <div className="min-w-0">
              <p className="font-oranienbaum font-bold text-white/70 text-sm truncate">{loading ? '...' : f?.value}</p>
              <p className="font-oranienbaum text-white/30 text-[9px] truncate">{f?.label}</p>
            </div>
          </div>
        ))}
      </div>
      {/* View Tabs */}
      <div className="flex gap-1 p-1 rounded-xl mb-5 overflow-x-auto"
        style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)' }}>
        {VIEWS?.map(v => (
          <button key={v} onClick={() => setView(v)}
            className={`px-4 py-2 rounded-lg text-xs font-oranienbaum whitespace-nowrap transition-all ${view === v ? 'text-black font-semibold' : 'text-white/50 hover:text-white/80'}`}
            style={view === v ? { background: '#D6EF4E' } : {}}>
            {v}
          </button>
        ))}
      </div>
      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6 mb-8">
        {/* Main content */}
        <div className="xl:col-span-2 rounded-2xl p-5"
          style={{ background: 'rgba(255,255,255,0.025)', border: '1px solid rgba(255,255,255,0.07)' }}>
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Icon name="LineChart" size={15} className="text-[#60A5FA]" />
              <h3 className="font-oranienbaum font-semibold text-white/70 text-sm">{view}</h3>
            </div>
            {view === 'Gastos Recurrentes' && (
              <div className="flex items-center gap-2">
                <button onClick={handleGenerarPrevisiones} disabled={generando}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-oranienbaum disabled:opacity-50"
                  style={{ background: 'rgba(214,239,78,0.10)', border: '1px solid rgba(214,239,78,0.25)', color: '#D6EF4E' }}>
                  <Icon name="Zap" size={12} />
                  {generando ? 'Generando...' : 'Generar Previsiones 90d'}
                </button>
                <button onClick={() => setShowRecurrenteForm(true)}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-oranienbaum font-semibold text-black"
                  style={{ background: '#D6EF4E' }}>
                  <Icon name="Plus" size={12} />Nuevo
                </button>
              </div>
            )}
            {view !== 'Gastos Recurrentes' && (
              <span className="px-2 py-0.5 rounded-full text-[10px] font-oranienbaum"
                style={{ background: 'rgba(214,239,78,0.10)', border: '1px solid rgba(214,239,78,0.20)', color: '#D6EF4E' }}>
                REAL vs PREVISTO
              </span>
            )}
          </div>

          {view === 'Previsión de Caja' && chartData?.length > 0 ? (
            <ResponsiveContainer width="100%" height={220}>
              <AreaChart data={chartData}>
                <defs>
                  <linearGradient id="saldoGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#60A5FA" stopOpacity={0.3} />
                    <stop offset="95%" stopColor="#60A5FA" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
                <XAxis dataKey="fecha" tick={{ fill: 'rgba(255,255,255,0.30)', fontSize: 10, fontFamily: 'Oranienbaum' }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fill: 'rgba(255,255,255,0.30)', fontSize: 10, fontFamily: 'Oranienbaum' }} axisLine={false} tickLine={false} />
                <Tooltip contentStyle={{ background: '#1A1A1A', border: '1px solid rgba(255,255,255,0.10)', borderRadius: 8, fontFamily: 'Oranienbaum', fontSize: 11 }} formatter={v => [`${fmt(v)} €`]} />
                <Area type="monotone" dataKey="saldo" stroke="#60A5FA" fill="url(#saldoGrad)" strokeWidth={2} name="Saldo Previsto" />
              </AreaChart>
            </ResponsiveContainer>
          ) : view === 'Posición Diaria' && movimientos?.length > 0 ? (
            <div className="space-y-2 max-h-64 overflow-y-auto">
              {movimientos?.slice(0, 20)?.map((m, i) => (
                <div key={m?.id || i} className="flex items-center justify-between py-2 border-b border-white/5 last:border-0">
                  <div className="flex items-center gap-3">
                    <div className={`w-2 h-2 rounded-full ${m?.tipo === 'INGRESO' ? 'bg-[#34D399]' : 'bg-[#F87171]'}`} />
                    <div>
                      <p className="font-oranienbaum text-white/70 text-xs">{m?.concepto}</p>
                      <p className="font-oranienbaum text-white/30 text-[10px]">{m?.fecha} · {m?.cuenta?.nombre || '—'}</p>
                    </div>
                  </div>
                  <span className={`font-oranienbaum font-semibold text-xs ${m?.tipo === 'INGRESO' ? 'text-[#34D399]' : 'text-[#F87171]'}`}>
                    {m?.tipo === 'INGRESO' ? '+' : '-'}{fmtEur(m?.tipo === 'INGRESO' ? m?.entrada : m?.salida)}
                  </span>
                </div>
              ))}
            </div>
          ) : view === 'Gastos Recurrentes' ? (
            <div className="space-y-2 max-h-96 overflow-y-auto">
              {recurrentes?.length === 0 ? (
                <div className="flex flex-col items-center gap-3 py-8">
                  <Icon name="Repeat" size={28} className="text-white/15" />
                  <p className="font-oranienbaum text-white/25 text-xs text-center">No hay gastos recurrentes configurados</p>
                  <button onClick={() => setShowRecurrenteForm(true)}
                    className="px-4 py-2 rounded-xl text-xs font-oranienbaum font-semibold text-black"
                    style={{ background: '#D6EF4E' }}>
                    Configurar primer gasto recurrente
                  </button>
                </div>
              ) : recurrentes?.map((r, i) => {
                const costeMensual = r?.importe_habitual / ({ MENSUAL: 1, BIMESTRAL: 2, TRIMESTRAL: 3, SEMESTRAL: 6, ANUAL: 12 }?.[r?.periodicidad_imputacion || r?.periodicidad] || 1);
                return (
                  <div key={r?.id || i} className="flex items-center justify-between py-3 px-3 rounded-xl border-b border-white/5 last:border-0 hover:bg-white/3 transition-colors"
                    style={{ border: '1px solid rgba(255,255,255,0.04)' }}>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-0.5">
                        <p className="font-oranienbaum text-white/80 text-xs font-semibold truncate">{r?.nombre}</p>
                        <span className="px-1.5 py-0.5 rounded text-[9px] font-oranienbaum flex-shrink-0"
                          style={{ background: r?.tipo === 'FIJO' ? 'rgba(251,191,36,0.12)' : 'rgba(167,139,250,0.12)', color: r?.tipo === 'FIJO' ? '#FBBF24' : '#A78BFA' }}>
                          {r?.tipo}
                        </span>
                        {!r?.activo && <span className="px-1.5 py-0.5 rounded text-[9px] font-oranienbaum" style={{ background: 'rgba(248,113,113,0.12)', color: '#F87171' }}>INACTIVO</span>}
                      </div>
                      <p className="font-oranienbaum text-white/40 text-[10px]">
                        {r?.proveedor || '—'} · {r?.categoria?.replace(/_/g, ' ')} · {r?.periodicidad_pago || r?.periodicidad}
                        {r?.centro_coste?.nombre ? ` · ${r?.centro_coste?.nombre}` : ''}
                      </p>
                      <p className="font-oranienbaum text-white/25 text-[9px] mt-0.5">
                        Próx: {r?.proximo_vencimiento || '—'} · Coste/mes: {fmtEur(costeMensual)}
                      </p>
                    </div>
                    <div className="flex items-center gap-3 ml-3">
                      <div className="text-right">
                        <p className="font-oranienbaum font-semibold text-white/80 text-sm">{fmtEur(r?.importe_habitual * (1 + r?.iva_porcentaje / 100))}</p>
                        <p className="font-oranienbaum text-white/30 text-[9px]">c/IVA</p>
                      </div>
                      <div className="flex items-center gap-1">
                        <button onClick={() => setEditingRecurrente(r)}
                          className="w-7 h-7 rounded-lg flex items-center justify-center text-white/30 hover:text-white/70 hover:bg-white/8 transition-all">
                          <Icon name="Edit" size={12} />
                        </button>
                        <button onClick={() => handleDeleteRecurrente(r?.id)}
                          className="w-7 h-7 rounded-lg flex items-center justify-center text-white/20 hover:text-red-400 hover:bg-red-400/8 transition-all">
                          <Icon name="Trash2" size={12} />
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="h-48 rounded-xl flex flex-col items-center justify-center gap-3"
              style={{ background: 'rgba(255,255,255,0.02)', border: '1px dashed rgba(255,255,255,0.08)' }}>
              <Icon name="LineChart" size={28} className="text-white/10" />
              <p className="font-oranienbaum text-white/25 text-sm">Sin movimientos en el período</p>
              <button onClick={() => setShowMovForm(true)} className="px-4 py-2 rounded-xl text-xs font-oranienbaum font-semibold text-black" style={{ background: '#D6EF4E' }}>
                Registrar movimiento
              </button>
            </div>
          )}
        </div>

        {/* Right panel */}
        <div className="space-y-4">
          {/* Accounts */}
          <div className="rounded-2xl p-4" style={{ background: 'rgba(255,255,255,0.025)', border: '1px solid rgba(255,255,255,0.07)' }}>
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <Icon name="Landmark" size={14} className="text-[#D6EF4E]" />
                <h3 className="font-oranienbaum font-semibold text-white/70 text-sm">Cuentas</h3>
              </div>
              <button onClick={() => setShowCuentaForm(true)} className="text-[#D6EF4E]/60 hover:text-[#D6EF4E] transition-colors">
                <Icon name="Plus" size={14} />
              </button>
            </div>
            {cuentas?.length === 0 ? (
              <p className="font-oranienbaum text-white/25 text-xs text-center py-4">Sin cuentas configuradas</p>
            ) : (
              <div className="space-y-2">
                {cuentas?.map((c, i) => (
                  <div key={c?.id || i} className="flex items-center justify-between py-2 border-b border-white/5 last:border-0">
                    <div>
                      <p className="font-oranienbaum text-white/70 text-xs">{c?.nombre}</p>
                      <p className="font-oranienbaum text-white/30 text-[10px]">{c?.tipo?.replace(/_/g, ' ')}</p>
                    </div>
                    <span className="font-oranienbaum font-semibold text-white/70 text-xs">{fmtEur(c?.saldo_actual)}</span>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Upcoming previews */}
          <div className="rounded-2xl p-4" style={{ background: 'rgba(255,255,255,0.025)', border: '1px solid rgba(255,255,255,0.07)' }}>
            <div className="flex items-center gap-2 mb-3">
              <Icon name="Calendar" size={14} className="text-[#34D399]" />
              <h3 className="font-oranienbaum font-semibold text-white/70 text-sm">Próximas Previsiones</h3>
            </div>
            <div className="space-y-2">
              {(previsiones?.previsiones || [])?.slice(0, 6)?.map((p, i) => (
                <div key={i} className="flex items-center justify-between py-1.5 border-b border-white/5 last:border-0">
                  <div>
                    <p className="font-oranienbaum text-white/60 text-xs">{p?.nombre}</p>
                    <p className="font-oranienbaum text-white/25 text-[10px]">{p?.fecha} · {p?.categoria?.replace(/_/g, ' ')}</p>
                  </div>
                  <span className="font-oranienbaum text-[#F87171] text-xs font-semibold">-{fmtEur(p?.total)}</span>
                </div>
              ))}
              {(previsiones?.previsiones || [])?.length === 0 && (
                <p className="font-oranienbaum text-white/25 text-xs text-center py-3">Sin previsiones · Configura gastos recurrentes</p>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FinTesoreria;
